import { useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  createNewPlayer, checkIn, checkInWithPriority, setPlayerTeam,
  addPriority, removePriority, toggleStayIn, checkOut,
  addScoreToBoard, cancelScoreToBoard, resetScore, setPointsHistory, registerPointRally,
  startGameCounter, stopGameCounter, incrementQueueWaiting,
  resolveGame, resolveGameTeams, addMatchToHistory,
} from 'redux/slices';
import { suggestNextTeam } from '../utils/suggestTeam';

const WORKER_URL = process.env.REACT_APP_WORKER_URL || 'wss://volleyball-scoreboard.pandlr.workers.dev';

// Roda no APP (fonte da verdade), em qualquer aba. Publica o estado, aplica comandos
// e aplica o placar remoto no Redux (para marcar mesmo fora da aba Marcador).
export function useRemoteSync() {
  const dispatch = useDispatch();
  const players = useSelector((state) => state.players);
  const teams = useSelector((state) => state.teams);
  const game = useSelector((state) => state.game);
  const { remoteControlEnabled, pointsHistory } = game;

  const wsRef = useRef(null);
  const reconnectRef = useRef(null);
  const playersRef = useRef(players);
  const snapshotRef = useRef(null);
  const pointsHistoryRef = useRef(pointsHistory);
  const gameStartedRef = useRef(game.gameStarted);
  const gameRef = useRef(game);
  const teamsRef = useRef(teams);

  useEffect(() => { playersRef.current = players; }, [players]);
  useEffect(() => { pointsHistoryRef.current = pointsHistory; }, [pointsHistory]);
  useEffect(() => { gameStartedRef.current = game.gameStarted; }, [game.gameStarted]);
  useEffect(() => { gameRef.current = game; }, [game]);
  useEffect(() => { teamsRef.current = teams; }, [teams]);

  // Sugestão de próximo time (balanceada) calculada pelo app.
  const nextTeamSuggestion = suggestNextTeam({
    players,
    playersPerTeam: game.playersPerTeam,
    genderBalanceMode: game.genderBalanceMode,
    genderRestrictedMode: game.genderRestrictedMode,
    femalesPerTeam: game.femalesPerTeam,
    autoBalanceMode: game.autoBalanceMode,
  });

  // Snapshot atual — inclui o placar (o app/tablet é a fonte da verdade).
  const snapshot = {
    players,
    teams,
    nextTeamSuggestion,
    game: {
      playersPerTeam: game.playersPerTeam,
      genderBalanceMode: game.genderBalanceMode,
      genderRestrictedMode: game.genderRestrictedMode,
      femalesPerTeam: game.femalesPerTeam,
      setterRestrictedMode: game.setterRestrictedMode,
      autoBalanceMode: game.autoBalanceMode,
      gameStarted: game.gameStarted,
      scoreA: game.scoreA,
      scoreB: game.scoreB,
    },
  };
  snapshotRef.current = snapshot;

  const publish = () => {
    const ws = wsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify({ type: 'state', data: snapshotRef.current }));
    }
  };

  // Marca/desmarca ponto no Redux (aplicado pelo app, que é a fonte da verdade).
  const addPoint = (team) => {
    if (!gameStartedRef.current) return;
    dispatch(addScoreToBoard(team));
    const next = [...pointsHistoryRef.current, team];
    pointsHistoryRef.current = next;
    dispatch(setPointsHistory(next));
    dispatch(registerPointRally());
  };
  const removePoint = (team) => {
    dispatch(cancelScoreToBoard(team));
    const next = [...pointsHistoryRef.current];
    next.pop();
    pointsHistoryRef.current = next;
    dispatch(setPointsHistory(next));
  };

  // Aplica um comando vindo do remoto.
  const applyCommand = (cmd) => {
    if (!cmd || !cmd.type) return;
    switch (cmd.type) {
      case 'addScore':
        addPoint(cmd.payload);
        break;
      case 'removeScore':
        removePoint(cmd.payload);
        break;
      case 'resetScore':
        dispatch(resetScore());
        dispatch(setPointsHistory([]));
        pointsHistoryRef.current = [];
        break;
      case 'createNewPlayer': {
        const list = playersRef.current;
        const playerId = list.length ? Math.max(...list.map((p) => p.playerId)) + 1 : 1;
        dispatch(createNewPlayer({
          playerId,
          playerName: cmd.payload.playerName,
          totalPoints: Math.min(100, Math.max(0, Number(cmd.payload.totalPoints) || 0)),
          gender: cmd.payload.gender || 'M',
        }));
        break;
      }
      case 'checkIn':
        dispatch(checkIn(cmd.payload));
        break;
      case 'checkInWithPriority':
        dispatch(checkInWithPriority(cmd.payload));
        break;
      case 'setPlayerTeam':
        dispatch(setPlayerTeam({ playerId: cmd.payload.playerId, teamId: cmd.payload.teamId }));
        break;
      case 'fillTeam': {
        // Preenche o time com vaga (auto) com os jogadores escolhidos.
        const list = playersRef.current;
        const perTeam = snapshotRef.current.game.playersPerTeam;
        const aLen = list.filter((p) => p.teamId === 1).length;
        const bLen = list.filter((p) => p.teamId === 2).length;
        let target = null;
        if (aLen < perTeam && aLen <= bLen) target = 1;
        else if (bLen < perTeam) target = 2;
        else if (aLen < perTeam) target = 1;
        if (target) {
          const vacancy = perTeam - (target === 1 ? aLen : bLen);
          (cmd.payload.playerIds || []).slice(0, vacancy).forEach((playerId) => {
            dispatch(setPlayerTeam({ playerId, teamId: target }));
          });
        }
        break;
      }
      case 'applyAdjustment':
        (cmd.payload.setTeamOps || []).forEach((op) => dispatch(setPlayerTeam(op)));
        (cmd.payload.priorityIds || []).forEach((playerId) => dispatch(addPriority(playerId)));
        break;
      case 'addPriority':
        dispatch(addPriority(cmd.payload));
        break;
      case 'removePriority':
        dispatch(removePriority(cmd.payload));
        break;
      case 'toggleStayIn':
        dispatch(toggleStayIn(cmd.payload));
        break;
      case 'checkOut':
        dispatch(checkOut(cmd.payload));
        break;
      case 'startGame': {
        const list = playersRef.current;
        const perTeam = snapshotRef.current.game.playersPerTeam;
        const aFull = list.filter((p) => p.teamId === 1).length === perTeam;
        const bFull = list.filter((p) => p.teamId === 2).length === perTeam;
        if (aFull && bFull && !gameStartedRef.current) {
          dispatch(incrementQueueWaiting());
          dispatch(startGameCounter());
        }
        break;
      }
      case 'endGame': {
        const g = gameRef.current;
        if (!g.gameStarted) break;
        const list = playersRef.current;
        const tms = teamsRef.current;
        const WINNER = g.scoreA > g.scoreB ? 1 : 2;
        const LOSER = WINNER === 1 ? 2 : 1;
        const params = {
          winnerTeamId: WINNER,
          loserTeamId: LOSER,
          winnerStays: true,
          loserStays: false,
          saveWinner: false,
        };
        const getPts = (p) => p.totalPoints ?? (p.wins - p.losses);
        const snapTeam = (teamId) => {
          const tp = list.filter((p) => p.teamId === teamId);
          return {
            name: tms.find((t) => t.id === teamId)?.name || `Time ${teamId === 1 ? 'A' : 'B'}`,
            points: tp.reduce((s, p) => s + getPts(p), 0),
            players: tp.map((p) => ({ playerName: p.playerName, gender: p.gender })),
          };
        };
        dispatch(addMatchToHistory({
          playedAt: Date.now(),
          scoreA: g.scoreA,
          scoreB: g.scoreB,
          winner: WINNER === 1 ? 'A' : 'B',
          durationSeconds: (g.time.minutes * 60) + g.time.seconds,
          maxRallySeconds: g.maxRallySeconds,
          teamA: snapTeam(1),
          teamB: snapTeam(2),
        }));
        dispatch(stopGameCounter());
        dispatch(resolveGameTeams(params));
        dispatch(resolveGame(params));
        dispatch(setPointsHistory([WINNER === 1 ? 'A' : 'B']));
        break;
      }
      default:
        break;
    }
  };

  // Conexão + aplicação de comandos/placar.
  useEffect(() => {
    if (!remoteControlEnabled) return undefined;

    let closedByUs = false;

    const connect = () => {
      const ws = new WebSocket(WORKER_URL);
      wsRef.current = ws;

      ws.onopen = () => {
        publish(); // envia o estado atual (inclui placar) assim que conecta
      };

      ws.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data);
          if (message.type === 'command') applyCommand(message.data);
        } catch (err) {
          // ignora mensagens inválidas
        }
      };

      ws.onclose = () => {
        wsRef.current = null;
        if (!closedByUs) reconnectRef.current = setTimeout(connect, 3000);
      };
    };

    connect();

    return () => {
      closedByUs = true;
      if (reconnectRef.current) clearTimeout(reconnectRef.current);
      if (wsRef.current) wsRef.current.close();
      wsRef.current = null;
    };
  }, [remoteControlEnabled]);

  // Publica o snapshot sempre que jogadores/times/config mudarem.
  useEffect(() => {
    if (!remoteControlEnabled) return;
    publish();
  }, [
    remoteControlEnabled,
    players,
    teams,
    game.playersPerTeam,
    game.genderBalanceMode,
    game.genderRestrictedMode,
    game.femalesPerTeam,
    game.setterRestrictedMode,
    game.autoBalanceMode,
    game.gameStarted,
    game.scoreA,
    game.scoreB,
  ]);
}
